import React, { useMemo, useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Search, X } from "lucide-react";
import { ATTACKERS, DEFENDERS } from "@/data/operators";

export const ExcludePanel = ({ excluded, onToggle, onClear }) => {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filter = (arr) =>
      !q ? arr : arr.filter((o) => o.name.toLowerCase().includes(q) || o.unit.toLowerCase().includes(q));
    return { attackers: filter(ATTACKERS), defenders: filter(DEFENDERS) };
  }, [query]);

  const renderList = (list, side) => {
    const accent = side === "attacker" ? "#E67300" : "#1F88E8";
    return (
      <div className="space-y-1">
        <div
          className="flex items-center justify-between text-[10px] tracking-[0.3em] font-bold uppercase py-2 px-1 sticky top-0 bg-neutral-950 z-10"
          style={{ color: accent, fontFamily: '"JetBrains Mono", monospace' }}
        >
          <span>{side}S</span>
          <span className="text-neutral-600">{list.length}</span>
        </div>
        {list.length === 0 && (
          <div className="text-xs text-neutral-600 py-2 px-1">No matches</div>
        )}
        {list.map((op) => {
          const isExcluded = excluded.has(op.id);
          return (
            <label
              key={op.id}
              data-testid={`exclude-${op.id}`}
              className="flex items-center gap-3 px-2 py-1.5 cursor-pointer hover:bg-neutral-900 transition-colors"
            >
              <Checkbox
                checked={isExcluded}
                onCheckedChange={() => onToggle(op.id)}
                className="rounded-none border-neutral-700 data-[state=checked]:bg-[#FFC800] data-[state=checked]:text-black data-[state=checked]:border-[#FFC800]"
              />
              <span
                className={`text-sm ${isExcluded ? "text-neutral-500 line-through" : "text-neutral-200"}`}
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                {op.name}
              </span>
              <span
                className="ml-auto text-[10px] tracking-widest uppercase text-neutral-600"
                style={{ fontFamily: '"JetBrains Mono", monospace' }}
              >
                {op.unit}
              </span>
            </label>
          );
        })}
      </div>
    );
  };

  return (
    <div className="border border-neutral-800 bg-neutral-950/60">
      <div className="flex items-center justify-between border-b border-neutral-800 px-4 py-3">
        <div
          className="text-xs font-bold tracking-[0.3em] uppercase text-neutral-400"
          style={{ fontFamily: '"JetBrains Mono", monospace' }}
        >
          Exclusions
        </div>
        <div className="flex items-center gap-3">
          <span
            className="text-[10px] tracking-widest text-neutral-500"
            style={{ fontFamily: '"JetBrains Mono", monospace' }}
            data-testid="excluded-count"
          >
            {excluded.size} OFF
          </span>
          {excluded.size > 0 && (
            <button
              onClick={onClear}
              data-testid="clear-exclusions-btn"
              className="text-[10px] tracking-widest uppercase text-neutral-400 hover:text-white flex items-center gap-1"
              style={{ fontFamily: '"JetBrains Mono", monospace' }}
            >
              <X size={10} /> CLEAR
            </button>
          )}
        </div>
      </div>

      <div className="p-3 border-b border-neutral-800">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-600" />
          <Input
            data-testid="exclude-search-input"
            placeholder="Search operators..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-8 h-9 rounded-none bg-neutral-900 border-neutral-800 text-neutral-200 placeholder:text-neutral-600 focus-visible:ring-0 focus-visible:border-[#FFC800]"
            style={{ fontFamily: "Manrope, sans-serif" }}
          />
        </div>
      </div>

      <ScrollArea className="h-[380px]">
        <div className="p-3 space-y-4">
          {renderList(filtered.attackers, "attacker")}
          {renderList(filtered.defenders, "defender")}
        </div>
      </ScrollArea>
    </div>
  );
};

export default ExcludePanel;
